// Ficha de una renta por pasos: lo que falta para entregar, pago, contrato,
// depósito, entrega, devolución, cargos, cierre e historial. Cada botón llama
// un paso del servidor, que revisa las reglas (la pantalla solo guía).
import { el, clear } from '../../utils/dom.js';
import { Rentals, Uploads, Documents } from '../../api/resources.js';
import { fmtDateTime, fmtMoney } from '../../utils/format.js';
import { openModal, confirm } from '../../components/modal.js';
import { field, readForm } from '../../components/form.js';
import { ok, err } from '../../components/toast.js';
import { openFile } from '../../api/client.js';

const PASOS = [
  ['pendiente', 'Por confirmar'],
  ['confirmada', 'Confirmada'],
  ['en_curso', 'Entregada'],
  ['devuelta', 'Devuelta'],
  ['cerrada', 'Cerrada'],
];

const EVENTO = {
  cotizada: 'Cotización creada', creada: 'Renta creada', solicitud_web: 'Llegó por el catálogo web',
  solicitud_aprobada: 'Solicitud aprobada', solicitud_vencida: 'Venció el apartado (no se aprobó a tiempo)', apartada: 'Equipos apartados', pago: 'Pago registrado',
  confirmada_pago: 'Confirmada (pagada completa)', confirmada_credito: 'Confirmada a crédito',
  contrato_creado: 'Contrato creado', contrato_enviado: 'Contrato enviado', contrato_visto: 'El cliente abrió el contrato',
  contrato_firmado: 'El cliente firmó el contrato', contrato_anulado: 'Contrato anulado',
  contrato_no_requerido: 'Se marcó sin contrato', contrato_requerido: 'Se volvió a pedir contrato',
  deposito_recibir: 'Depósito recibido', deposito_devolver: 'Depósito devuelto', deposito_aplicar: 'Depósito aplicado a la renta',
  entregada: 'Equipos entregados', asignada: 'Tarea asignada', preparada: 'Equipos preparados', devolucion_parcial: 'Devolución parcial', devuelta: 'Equipos devueltos',
  extendida: 'Renta extendida', reembolso: 'Dinero devuelto al cliente',
  multa: 'Cargo agregado', cerrada: 'Renta cerrada', cancelada: 'Renta cancelada', mensaje_cliente: 'Mensaje al cliente por WhatsApp',
};

const METODOS = [
  ['efectivo', 'Efectivo'], ['transferencia', 'Transferencia'], ['tarjeta', 'Tarjeta'],
  ['deposito', 'Depósito bancario'], ['cheque', 'Cheque'], ['otro', 'Otro'],
];
const ESTADOS = [['bien', 'Bien'], ['detalle', 'Con detalle'], ['danado', 'Dañado'], ['faltante', 'Faltante']];
const TIPOS_CARGO = [['atraso', 'Atraso'], ['dano', 'Daño'], ['accesorio', 'Accesorio faltante'], ['limpieza', 'Limpieza'], ['perdida', 'Pérdida'], ['otro', 'Otro']];

export default async function RentalsDetailPage(outlet, ctx) {
  clear(outlet);
  const id = ctx.params.id;
  let r;
  let cfg = { moneda: 'DOP', empresa: '' };
  try { [r, cfg] = await Promise.all([Rentals.get(id), Rentals.config().catch(() => cfg)]); }
  catch (ex) { outlet.innerHTML = `<div class="empty">Error: ${ex.message}</div>`; return; }

  const $ = (n) => fmtMoney(n, cfg.moneda || 'DOP');
  const etapa = r.etapa;
  const saldo = Math.max(0, Number(r.total) - Number(r.paid_amount));
  const deposito = Number(r.deposit);
  const depRecibido = Number(r.deposito_recibido);
  const depQueda = depRecibido - Number(r.deposito_devuelto) - Number(r.deposito_aplicado);
  const atrasada = etapa === 'en_curso' && new Date(r.end_date) < new Date();
  const contrato = r.contrato;
  const firmado = contrato?.estado === 'firmado';
  const enlaceFirma = contrato ? `${location.origin}/firmar/${contrato.token}` : null;
  const reload = () => RentalsDetailPage(outlet, ctx);

  async function correr(fn, exito) {
    try { await fn(); if (exito) ok(exito); reload(); }
    catch (ex) { err(ex.message); }
  }

  // ---------------------------------------------------------------- cabecera
  outlet.appendChild(el('div', { class: 'page-head' },
    el('div', {},
      el('h1', {}, r.code, r.origen === 'web' ? el('span', { class: 'badge info', style: 'margin-left:8px;vertical-align:middle' }, 'Web') : null),
      el('div', { class: 'muted' }, `${r.client_name || 'Cliente'} · ${fmtDateTime(r.start_date)} → ${fmtDateTime(r.end_date)}`),
    ),
    el('div', { class: 'flex gap-2 wrap' },
      el('a', { href: '#/rentals', class: 'btn ghost' }, '← Rentas'),
      el('button', { class: 'btn', onclick: () => openFile(Documents.rentalContractPath(id)).catch((ex) => err(ex.message)) }, 'PDF'),
    ),
  ));

  // ---------------------------------------------------------------- pasos
  if (etapa === 'cancelada') {
    outlet.appendChild(el('div', { class: 'card mb-4' }, el('strong', {}, 'Cancelada'),
      r.motivo_cancelacion ? el('span', { class: 'muted' }, ` · ${r.motivo_cancelacion}`) : null));
  } else if (etapa === 'cotizacion') {
    outlet.appendChild(el('div', { class: 'card mb-4 rp-aviso' },
      el('div', {}, el('strong', {}, 'Es una cotización. '), 'Todavía no aparta los equipos: otro cliente puede rentarlos en esas fechas.'),
      el('button', { class: 'btn primary', onclick: () => correr(() => Rentals.apartar(id), 'Equipos apartados') }, 'Apartar equipos'),
    ));
  } else {
    const actual = PASOS.findIndex(([e]) => e === etapa);
    outlet.appendChild(el('ol', { class: 'rp-pasos card mb-4', 'aria-label': 'Pasos de la renta' },
      ...PASOS.map(([, label], i) => el('li', { class: i < actual ? 'hecho' : i === actual ? 'actual' : '' },
        el('span', { class: 'rp-num' }, i < actual ? '✓' : String(i + 1)),
        el('span', { class: 'rp-lbl' }, label)))));
  }
  // Solicitud que llegó por el catálogo web: la empresa la aprueba antes de entregar.
  if (r.origen === 'web' && !r.aprobada_at && ['pendiente', 'confirmada'].includes(etapa)) {
    const vence = r.apartado_vence ? new Date(r.apartado_vence) : null;
    outlet.appendChild(el('section', { class: 'card mb-4 rp-aviso' },
      el('div', {},
        el('strong', {}, 'Solicitud del catálogo web. '),
        vence ? `Los equipos quedan apartados hasta el ${fmtDateTime(vence)}; si no la apruebas, se suelta sola.` : 'Revísala y apruébala.',
        r.solicitud?.notas ? el('div', { class: 'text-sm muted mt-3' }, `Nota del cliente: ${r.solicitud.notas}`) : null),
      el('div', { class: 'flex gap-2 wrap' },
        el('button', { class: 'btn primary', onclick: () => correr(() => Rentals.aprobar(id), 'Solicitud aprobada') }, 'Aprobar solicitud'),
        el('button', { class: 'btn', onclick: () => {
          const motivo = prompt('¿Por qué la rechazas? (lo ves en el historial)');
          if (motivo === null) return;
          correr(() => Rentals.cancel(id, `Rechazada: ${motivo || 'sin motivo'}`), 'Solicitud rechazada');
        } }, 'Rechazar'))));
  }
  if (atrasada) {
    outlet.appendChild(el('div', { class: 'rp-alerta mb-4' },
      `Debía devolverse el ${fmtDateTime(r.end_date)}. Sigue ocupando el equipo hasta que lo registres devuelto.`));
  }

  const main = el('div', { class: 'rp-main' });
  const aside = el('aside', { class: 'rp-aside' });
  outlet.appendChild(el('div', { class: 'rp-grid' }, main, aside));

  // ---------------------------------------------------------------- para entregar
  if (['pendiente', 'confirmada'].includes(etapa)) {
    const checks = [
      ...(r.origen === 'web' ? [[!!r.aprobada_at, r.aprobada_at ? 'Solicitud aprobada' : 'Falta aprobar la solicitud web']] : []),
      [etapa === 'confirmada', etapa === 'confirmada' ? (r.usa_credito ? 'Confirmada a crédito' : 'Pagada completa') : `Falta cobrar ${$(saldo)}`],
      ...(r.contrato_requerido ? [[firmado, firmado ? 'Contrato firmado' : 'Falta el contrato firmado']] : []),
      ...(deposito > 0 ? [[depRecibido + 0.01 >= deposito, depRecibido + 0.01 >= deposito ? 'Depósito recibido' : `Falta recibir el depósito (${$(deposito - depRecibido)})`]] : []),
    ];
    main.appendChild(el('section', { class: 'card mb-4' },
      el('h3', { class: 'rp-h' }, 'Para entregar'),
      el('ul', { class: 'rp-checks' }, ...checks.map(([okk, t]) => el('li', { class: okk ? 'ok' : 'falta' }, el('span', {}, okk ? '✓' : '○'), t))),
      etapa === 'confirmada'
        ? el('button', { class: 'btn primary', disabled: (r.bloqueos || []).length > 0, onclick: abrirEntrega }, 'Entregar equipos')
        : null,
    ));
  }
  if (etapa === 'en_curso') {
    main.appendChild(el('section', { class: 'card mb-4' },
      el('h3', { class: 'rp-h' }, 'Devolución'),
      el('p', { class: 'muted' }, 'Registra lo que devuelve y cómo viene cada equipo. Puede ser por partes.'),
      el('button', { class: 'btn primary', onclick: abrirDevolucion }, 'Registrar devolución'),
    ));
  }

  // ---------------------------------------------------------------- equipos
  const revisiones = r.revisiones || [];
  main.appendChild(el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Equipos'),
    el('ul', { class: 'rp-lista' }, ...ordenarLineas(r.items || []).map((l) => {
      const revs = revisiones.filter((v) => v.rental_item_id === l.id);
      return el('li', {},
        el('div', { class: 'flex justify-between gap-2' },
          el('strong', {}, `${l.kit_line_id ? '↳ ' : ''}${l.item_name || 'Equipo'}${l.serial_number ? ` · S/N ${l.serial_number}` : ''}`),
          l.kit_line_id ? el('span', { class: 'text-sm muted' }, 'parte del combo') : el('span', {}, $(l.line_total))),
        el('div', { class: 'text-sm muted' }, l.kit_line_id
          ? `× ${l.quantity}${l.returned_quantity > 0 ? ` · devueltos ${l.returned_quantity}` : ''}`
          : `× ${l.quantity} · ${$(l.price_per_day)}/día${l.es_kit ? ' · combo' : ''}${l.returned_quantity > 0 ? ` · devueltos ${l.returned_quantity}` : ''}`),
        ...revs.map((v) => el('div', { class: 'text-sm' },
          el('strong', {}, v.fase === 'salida' ? 'Salida: ' : 'Regreso: '),
          `${(ESTADOS.find(([k]) => k === v.estado) || [, v.estado])[1].toLowerCase()}${v.fase === 'regreso' ? ` (${v.cantidad})` : ''}`,
          v.accesorios_completos ? '' : ' · faltan accesorios',
          v.notas ? el('span', { class: 'muted' }, ` · ${v.notas}`) : null)),
      );
    })),
  ));

  // ---------------------------------------------------------------- cargos
  const ultimaDev = (r.eventos || []).find((e) => e.accion === 'devuelta' || e.accion === 'devolucion_parcial');
  const atrasoMin = Number(ultimaDev?.detalle?.atraso_min || 0);
  const penalties = r.penalties || [];
  if (['en_curso', 'devuelta'].includes(etapa) || penalties.length) {
    main.appendChild(el('section', { class: 'card mb-4' },
      el('h3', { class: 'rp-h' }, 'Cargos y multas'),
      atrasoMin > 0 && !penalties.some((p) => p.type === 'atraso') && etapa !== 'cerrada'
        ? el('div', { class: 'rp-aviso-sm' }, `Devolvió ${tarde(atrasoMin)} tarde. Si corresponde, agrega un cargo por atraso.`) : null,
      penalties.length
        ? el('ul', { class: 'rp-lista' }, ...penalties.map((p) => el('li', { class: 'flex justify-between gap-2' },
          el('span', {}, p.description || p.type), el('span', {}, $(p.amount)))))
        : el('p', { class: 'muted' }, 'Sin cargos.'),
      ['en_curso', 'devuelta'].includes(etapa) ? el('button', { class: 'btn', onclick: abrirCargo }, 'Agregar cargo') : null,
    ));
  }

  // ---------------------------------------------------------------- historial
  main.appendChild(el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Historial'),
    (r.eventos || []).length
      ? el('ol', { class: 'rp-hist' }, ...(r.eventos || []).map((e) => el('li', {},
        el('time', {}, fmtDateTime(e.created_at)),
        el('span', {}, EVENTO[e.accion] || e.accion,
          typeof e.detalle?.monto === 'number' ? el('span', { class: 'muted' }, ` · ${$(e.detalle.monto)}`) : null,
          e.detalle?.descripcion ? el('span', { class: 'muted' }, ` · ${e.detalle.descripcion}`) : null,
          e.detalle?.motivo ? el('span', { class: 'muted' }, ` · ${e.detalle.motivo}`) : null,
          Number(e.detalle?.atraso_min || 0) > 0 ? el('span', { class: 'rp-rojo' }, ` · ${tarde(e.detalle.atraso_min)} tarde`) : null,
          e.actor ? el('span', { class: 'muted' }, ` · ${e.actor}`) : null))))
      : el('p', { class: 'muted' }, 'Sin movimientos.'),
  ));

  // ---------------------------------------------------------------- lateral: cliente
  aside.appendChild(el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Cliente'),
    el('div', { class: 'text-bold' }, r.client_name || '—'),
    r.client_phone ? el('div', { class: 'muted text-sm' }, r.client_phone) : null,
    r.client_email ? el('div', { class: 'muted text-sm' }, r.client_email) : null,
    el('div', { class: 'text-sm mt-3' }, el('span', { class: 'muted' }, 'Entrega: '), fmtDateTime(r.start_date)),
    el('div', { class: 'text-sm' }, el('span', { class: 'muted' }, 'Devolución: '), fmtDateTime(r.end_date)),
    r.actual_return_date ? el('div', { class: 'text-sm' }, el('span', { class: 'muted' }, 'Devuelta: '), fmtDateTime(r.actual_return_date)) : null,
  ));

  // ---------------------------------------------------------------- lateral: extender / devolver dinero
  if (['pendiente', 'confirmada', 'en_curso'].includes(etapa)) {
    aside.appendChild(el('button', { class: 'btn mb-4', onclick: () => {
      const form = el('form', { onsubmit: async (e) => {
        e.preventDefault();
        const d = readForm(form);
        try { await Rentals.extender(id, new Date(d.fin).toISOString()); ok('Renta extendida'); m.close(); reload(); }
        catch (ex) { err(ex.message); }
      } },
      el('p', { class: 'muted text-sm' }, 'Solo se extiende si el equipo está libre para los días nuevos. Se suma al total.'),
      field({ name: 'fin', label: 'Nueva fecha de devolución', type: 'datetime-local', required: true,
        value: localInput(new Date(new Date(r.end_date).getTime() + 864e5)) }));
      const m = openModal({ title: `Extender ${r.code}`, body: form, footer: [
        el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
        el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, 'Extender'),
      ] });
    } }, 'Extender la renta'));
  }
  const devolvible = etapa === 'cancelada' ? Number(r.paid_amount) : Math.max(0, Number(r.paid_amount) - Number(r.total));
  if (devolvible > 0.01) {
    aside.appendChild(el('section', { class: 'card mb-4' },
      el('h3', { class: 'rp-h' }, 'Devolver dinero'),
      el('p', { class: 'muted text-sm' }, etapa === 'cancelada' ? `La renta se canceló con ${$(devolvible)} cobrado.` : `Se cobró ${$(devolvible)} de más.`),
      el('button', { class: 'btn', onclick: () => {
        const form = el('form', { onsubmit: async (e) => {
          e.preventDefault();
          const d = readForm(form);
          try { await Rentals.reembolso(id, { monto: Number(d.monto), metodo: d.metodo, motivo: d.motivo || undefined }); ok('Reembolso registrado'); m.close(); reload(); }
          catch (ex) { err(ex.message); }
        } },
        field({ name: 'monto', label: 'Monto', type: 'number', step: '0.01', min: '0.01', max: devolvible.toFixed(2), value: devolvible.toFixed(2), required: true }),
        field({ name: 'metodo', label: 'Forma', type: 'select', value: 'transferencia', options: METODOS.map(([value, label]) => ({ value, label })) }),
        field({ name: 'motivo', label: 'Motivo' }));
        const m = openModal({ title: 'Devolver dinero', body: form, footer: [
          el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
          el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, 'Registrar'),
        ] });
      } }, 'Devolver dinero')));
  }

  // ---------------------------------------------------------------- lateral: equipo asignado
  if (!['cerrada', 'cancelada'].includes(etapa)) {
    const TAREA = { preparar: 'Preparar', entregar: 'Entregar', recibir: 'Recibir' };
    const asig = r.asignaciones || [];
    const sec = el('section', { class: 'card mb-4' }, el('h3', { class: 'rp-h' }, 'Equipo asignado'),
      asig.length ? el('ul', { class: 'rp-lista' }, ...asig.map((a) => el('li', { class: 'flex justify-between gap-2' },
        el('span', {}, `${a.nombre} · ${TAREA[a.tarea]}`, a.hecho_at ? el('span', { class: 'rp-ok' }, ' ✓') : null),
        el('button', { class: 'btn ghost', 'aria-label': 'Quitar', onclick: () => correr(() => Rentals.quitarAsignacion(id, a.id)) }, '✕'))))
        : el('p', { class: 'muted text-sm' }, 'Nadie asignado. Lo asignado lo ve en "Mis entregas", sin precios.'));
    const zona = el('div', { class: 'mt-3' });
    zona.appendChild(el('button', { class: 'btn', onclick: async () => {
      let gente = [];
      try { gente = await Rentals.equipo(); } catch (ex) { err(ex.message); return; }
      const pers = el('select', { class: 'select', 'aria-label': 'Persona' }, ...gente.map((g) => el('option', { value: g.id }, g.nombre)));
      const tarea = el('select', { class: 'select', 'aria-label': 'Tarea' }, ...Object.entries(TAREA).map(([v, t]) => el('option', { value: v }, t)));
      zona.replaceChildren(el('div', { class: 'flex gap-2 wrap' }, pers, tarea,
        el('button', { class: 'btn primary', onclick: () => correr(() => Rentals.asignar(id, pers.value, tarea.value), 'Asignado') }, 'Asignar')));
    } }, 'Asignar a alguien'));
    sec.appendChild(zona);
    aside.appendChild(sec);
  }

  // ---------------------------------------------------------------- lateral: mensajes al cliente
  const tel = String(r.client_phone || '').replace(/\D/g, '');
  if (tel.length >= 10 && !['cancelada'].includes(etapa)) {
    const nombre = (r.client_name || '').split(' ')[0];
    const MENSAJES = [
      ['confirmacion', 'Confirmar la renta', ['confirmada', 'pendiente'].includes(etapa),
        `Hola ${nombre}, por favor revisa tu renta ${r.code}: entrega ${fmtDateTime(r.start_date)}, devolución ${fmtDateTime(r.end_date)}. Total ${$(r.total)}${deposito > 0 ? ` y depósito de garantía ${$(deposito)}` : ''}. ¡Gracias!`],
      ['pago', 'Recordar el pago', saldo > 0.01 && ['pendiente', 'confirmada', 'en_curso', 'devuelta'].includes(etapa),
        `Hola ${nombre}, por favor recuerda el pago pendiente de tu renta ${r.code}: ${$(saldo)}. ¡Gracias!`],
      ['contrato', 'Enviar el contrato', !!contrato && !firmado,
        `Hola ${nombre}, por favor firma el contrato de tu renta ${r.code} aquí: ${enlaceFirma}`],
      ['entrega', 'Recordar la entrega', ['confirmada', 'pendiente'].includes(etapa),
        `Hola ${nombre}, te recordamos que tus equipos de la renta ${r.code} están listos para el ${fmtDateTime(r.start_date)}.`],
      ['devolucion', 'Recordar la devolución', etapa === 'en_curso',
        `Hola ${nombre}, por favor recuerda devolver los equipos de la renta ${r.code} el ${fmtDateTime(r.end_date)}. ¡Gracias!`],
      ['gracias', 'Dar las gracias', ['devuelta', 'cerrada'].includes(etapa),
        `Hola ${nombre}, gracias por rentar con nosotros. ¡Te esperamos pronto!`],
    ].filter((m) => m[2]);
    if (MENSAJES.length) {
      aside.appendChild(el('section', { class: 'card mb-4' },
        el('h3', { class: 'rp-h' }, 'Escribirle por WhatsApp'),
        el('p', { class: 'muted text-sm' }, 'Se abren en tu WhatsApp ya escritos; tú los envías.'),
        el('div', { class: 'flex gap-2 wrap' }, ...MENSAJES.map(([tipo, label, , texto]) => el('button', { class: 'btn', onclick: () => {
          window.open(`https://wa.me/${tel.length === 10 ? '1' + tel : tel}?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
          Rentals.mensaje(id, tipo).catch(() => {});
        } }, label)))));
    }
  }

  // ---------------------------------------------------------------- lateral: pago
  const pagoCard = el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Pago'),
    el('div', { class: 'rp-tres' },
      cifra('Total', $(r.total)), cifra('Pagado', $(r.paid_amount), 'ok'), cifra('Saldo', $(saldo), saldo > 0.01 ? 'warn' : '')),
  );
  if (saldo > 0.01 && !['cerrada', 'cancelada', 'cotizacion'].includes(etapa)) {
    pagoCard.appendChild(el('button', { class: 'btn primary mt-3', onclick: abrirPago }, 'Registrar pago'));
  }
  if (etapa === 'pendiente' && saldo > 0.01 && r.credito?.activo) {
    pagoCard.appendChild(el('div', { class: 'rp-sub' },
      el('p', { class: 'text-sm muted' }, `Cliente de confianza: límite ${$(r.credito.limite)}, debe ${$(r.credito.deuda)}`,
        r.credito.con_atraso ? el('strong', { class: 'rp-rojo' }, ' · tiene pagos vencidos') : null),
      el('button', { class: 'btn', onclick: async () => {
        if (await confirm({ title: 'Confirmar a crédito', message: `Le quedan ${$(r.credito.disponible)} de crédito. ¿Confirmar la renta sin cobrar todo?` })) {
          correr(() => Rentals.credito(id), 'Confirmada a crédito');
        }
      } }, 'Confirmar a crédito')));
  }
  if ((r.payments || []).length) {
    pagoCard.appendChild(el('ul', { class: 'rp-lista rp-sub' }, ...(r.payments || []).map((p) => el('li', { class: 'flex justify-between gap-2' },
      el('span', {}, $(p.amount), el('span', { class: 'muted text-sm' }, ` ${(METODOS.find(([k]) => k === p.method) || [, p.method])[1]} · ${fmtDateTime(p.paid_at)}`)),
      p.comprobante_url ? el('a', { href: p.comprobante_url, target: '_blank', rel: 'noopener', class: 'text-sm' }, 'Comprobante') : null))));
  }
  aside.appendChild(pagoCard);

  // ---------------------------------------------------------------- lateral: contrato
  const contratoCard = el('section', { class: 'card mb-4' }, el('h3', { class: 'rp-h' }, 'Contrato'));
  if (!r.contrato_requerido && !contrato) {
    contratoCard.appendChild(el('p', { class: 'muted' }, 'Esta renta no lleva contrato.'));
  } else if (!contrato) {
    contratoCard.appendChild(el('p', { class: 'muted text-sm' }, 'Al crearlo se lo mandamos al cliente (correo y WhatsApp, si están encendidos) para que lo firme desde su celular.'));
    if (!['cerrada', 'cancelada'].includes(etapa)) {
      contratoCard.appendChild(el('button', { class: 'btn primary', onclick: () => correr(() => Rentals.crearContrato(id), 'Contrato creado') }, 'Crear contrato'));
    }
  } else {
    const ESTADO_C = { borrador: 'Sin enviar', enviado: 'Enviado, sin abrir', visto: 'El cliente lo abrió', firmado: 'Firmado' };
    contratoCard.appendChild(el('p', { class: firmado ? 'rp-ok' : '' },
      firmado ? '✓ ' : '○ ', ESTADO_C[contrato.estado] || contrato.estado,
      firmado ? el('span', { class: 'muted' }, ` · ${contrato.firmado_nombre} · ${fmtDateTime(contrato.firmado_at)}`) : null));
    const botones = el('div', { class: 'flex gap-2 wrap' });
    if (!firmado) {
      botones.append(
        el('button', { class: 'btn primary', onclick: () => {
          const tel = String(r.client_phone || '').replace(/\D/g, '');
          const texto = `Hola ${r.client_name || ''}, por favor firma el contrato de tu renta ${r.code} aquí: ${enlaceFirma}`;
          window.open(`https://wa.me/${tel.length === 10 ? '1' + tel : tel}?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
          Rentals.contratoEnviado(id).then(reload).catch(() => {});
        } }, 'Enviar por WhatsApp'),
        el('button', { class: 'btn', onclick: async () => {
          try { await navigator.clipboard.writeText(enlaceFirma); ok('Enlace copiado'); }
          catch { prompt('Copia el enlace:', enlaceFirma); }
          Rentals.contratoEnviado(id).then(reload).catch(() => {});
        } }, 'Copiar enlace'),
      );
    }
    botones.append(el('a', { class: 'btn ghost', href: enlaceFirma, target: '_blank', rel: 'noopener' }, 'Ver contrato'));
    if (!firmado && !['cerrada', 'cancelada'].includes(etapa)) {
      botones.append(el('button', { class: 'btn ghost', onclick: async () => {
        if (await confirm({ title: 'Anular contrato', message: 'El enlace deja de servir. Después puedes crear uno nuevo.', danger: true, confirmText: 'Anular' })) {
          correr(() => Rentals.anularContrato(id), 'Contrato anulado');
        }
      } }, 'Anular'));
    }
    contratoCard.appendChild(botones);
  }
  if (['cotizacion', 'pendiente', 'confirmada'].includes(etapa) && !firmado) {
    contratoCard.appendChild(el('button', { class: 'btn ghost text-sm mt-3', onclick: async () => {
      const quitar = r.contrato_requerido;
      if (quitar && !await confirm({ title: 'Sin contrato', message: '¿Entregar esta renta SIN contrato firmado? Queda anotado en el historial.' })) return;
      correr(() => Rentals.contratoRequerido(id, !quitar));
    } }, r.contrato_requerido ? 'Esta renta no lleva contrato' : 'Pedir contrato otra vez'));
  }
  aside.appendChild(contratoCard);

  // ---------------------------------------------------------------- lateral: mensajes al cliente
  const msgCard = el('section', { class: 'card mb-4' }, el('h3', { class: 'rp-h' }, 'Lo que le llegó al cliente'));
  const enlaceCliente = r.token_publico ? `${location.origin}/renta/${r.token_publico}` : null;
  msgCard.appendChild(el('div', { class: 'flex gap-2 wrap' },
    etapa !== 'cancelada' ? el('button', { class: 'btn', onclick: () => correr(() => Rentals.enviarCliente(id), 'Resumen enviado al cliente') }, 'Enviar resumen / factura') : null,
    enlaceCliente ? el('a', { class: 'btn ghost', href: enlaceCliente, target: '_blank', rel: 'noopener' }, 'Lo que ve el cliente') : null));
  const msgLista = el('div', { class: 'rp-msgs mt-2' }, el('p', { class: 'muted text-sm' }, 'Cargando…'));
  msgCard.appendChild(msgLista);
  aside.appendChild(msgCard);
  Rentals.mensajesDe(id).then((ms) => {
    clear(msgLista);
    const delCliente = (ms || []).filter((x) => x.destinatario !== 'equipo');
    if (!delCliente.length) { msgLista.appendChild(el('p', { class: 'muted text-sm' }, 'Todavía no le ha llegado nada. Se encienden en Rentas → Ajustes → Mensajes y WhatsApp.')); return; }
    const EST = { enviado: '✓', fallido: '✕', omitido: '–', pendiente: '…', enviando: '…' };
    for (const x of delCliente.slice(0, 12)) {
      msgLista.appendChild(el('div', { class: `rp-msg rp-msg-${x.estado}`, title: x.error || '' },
        el('span', {}, `${x.canal === 'email' ? '✉️' : '💬'} ${x.asunto || x.evento}`),
        el('span', { class: 'muted text-sm' }, `${EST[x.estado] || ''} ${fmtDateTime(x.enviado_at || x.created_at)}`)));
    }
  }).catch(() => { clear(msgLista); });

  // ---------------------------------------------------------------- lateral: depósito
  if (deposito > 0 || depRecibido > 0) {
    const dep = el('section', { class: 'card mb-4' },
      el('h3', { class: 'rp-h' }, 'Depósito de garantía'),
      fila('Acordado', $(deposito)), fila('Recibido', $(depRecibido)),
      Number(r.deposito_devuelto) > 0 ? fila('Devuelto', $(r.deposito_devuelto)) : null,
      Number(r.deposito_aplicado) > 0 ? fila('Aplicado a cargos', $(r.deposito_aplicado)) : null,
    );
    const bs = el('div', { class: 'flex gap-2 wrap mt-3' });
    if (['cotizacion', 'pendiente', 'confirmada'].includes(etapa) && depRecibido + 0.01 < deposito) {
      bs.append(el('button', { class: 'btn primary', onclick: () => abrirDeposito('recibir', deposito - depRecibido) }, 'Recibir depósito'));
    }
    if (['en_curso', 'devuelta'].includes(etapa) && depQueda > 0.01) {
      if (saldo > 0.01) bs.append(el('button', { class: 'btn', onclick: () => abrirDeposito('aplicar', Math.min(depQueda, saldo)) }, 'Aplicar a la renta'));
      if (etapa === 'devuelta') bs.append(el('button', { class: 'btn', onclick: () => abrirDeposito('devolver', depQueda) }, 'Devolver al cliente'));
    }
    dep.appendChild(bs);
    aside.appendChild(dep);
  }

  // ---------------------------------------------------------------- lateral: cierre
  if (etapa === 'devuelta') {
    aside.appendChild(el('section', { class: 'card mb-4' },
      el('h3', { class: 'rp-h' }, 'Cerrar'),
      el('p', { class: 'muted text-sm' }, 'Se cierra con el saldo en cero y el depósito devuelto o aplicado.'),
      el('button', { class: 'btn primary', onclick: () => correr(() => Rentals.cerrar(id), 'Renta cerrada') }, 'Cerrar renta')));
  }
  if (['cotizacion', 'pendiente', 'confirmada'].includes(etapa)) {
    aside.appendChild(el('button', { class: 'btn ghost rp-rojo', onclick: async () => {
      const motivo = prompt('¿Por qué se cancela? Las fechas quedan libres.');
      if (motivo === null) return;
      correr(() => Rentals.cancel(id, motivo), 'Renta cancelada');
    } }, 'Cancelar renta'));
  }

  // ================================================================ modales
  function revisionCampos(l, conCantidad) {
    const pend = l.quantity - l.returned_quantity;
    return el('div', { class: 'rp-rev' },
      el('div', { class: 'text-bold' }, `${l.item_name || 'Equipo'}${l.serial_number ? ` · S/N ${l.serial_number}` : ''} × ${conCantidad ? pend : l.quantity}`),
      el('div', { class: 'form-grid' },
        conCantidad ? field({ name: `cant_${l.id}`, label: 'Devuelve', type: 'number', min: 0, max: pend, value: pend }) : null,
        field({ name: `estado_${l.id}`, label: 'Estado', type: 'select', required: true, value: 'bien', options: ESTADOS.map(([value, label]) => ({ value, label })) }),
      ),
      field({ name: `acc_${l.id}`, label: 'Accesorios completos', type: 'checkbox', value: true }),
      field({ name: `notas_${l.id}`, label: 'Notas (opcional)' }),
    );
  }
  function leerRevision(d, lineas, conCantidad) {
    return lineas.map((l) => ({
      rental_item_id: l.id,
      estado: d[`estado_${l.id}`] || 'bien',
      accesorios_completos: !!d[`acc_${l.id}`],
      notas: d[`notas_${l.id}`] || undefined,
      ...(conCantidad ? { cantidad: Number(d[`cant_${l.id}`] || 0) } : {}),
    }));
  }

  function abrirEntrega() {
    const lineas = (r.items || []).filter((l) => !l.es_kit);
    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      try { await Rentals.entregar(id, leerRevision(readForm(form), lineas, false)); ok('Equipos entregados'); m.close(); reload(); }
      catch (ex) { err(ex.message); }
    } },
    el('p', { class: 'muted text-sm' }, 'Revisa cada equipo al entregarlo. Queda guardado como revisión de salida.'),
    ...lineas.map((l) => revisionCampos(l, false)));
    const m = openModal({ title: `Entregar ${r.code}`, body: form, size: 'lg', footer: [
      el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
      el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, 'Entregar'),
    ] });
  }

  function abrirDevolucion() {
    const lineas = (r.items || []).filter((l) => !l.es_kit && l.quantity > l.returned_quantity);
    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      const d = readForm(form);
      try {
        const res = await Rentals.devolver(id, leerRevision(d, lineas, true).filter((x) => x.cantidad > 0),
          d.fecha ? new Date(d.fecha).toISOString() : undefined);
        const at = Number(res?.resultado?.atraso_min || 0);
        ok(at > 0 ? `Devolución registrada. Llegó ${tarde(at)} tarde.` : 'Devolución registrada');
        m.close(); reload();
      } catch (ex) { err(ex.message); }
    } },
    field({ name: 'fecha', label: 'Fecha y hora en que lo devolvió', type: 'datetime-local', value: localInput(new Date()) }),
    ...lineas.map((l) => revisionCampos(l, true)));
    const m = openModal({ title: `Devolución de ${r.code}`, body: form, size: 'lg', footer: [
      el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
      el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, 'Registrar devolución'),
    ] });
  }

  function abrirPago() {
    const archivo = el('input', { type: 'file', accept: 'image/*,application/pdf', class: 'input' });
    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      const d = readForm(form);
      try {
        let comprobante;
        const f = archivo.files?.[0];
        if (f) {
          const up = f.type === 'application/pdf' ? await Uploads.documents([f]) : await Uploads.images([f]);
          comprobante = up?.files?.[0]?.url;
        }
        await Rentals.pagar(id, {
          amount: Number(d.amount), method: d.method, reference: d.reference || undefined,
          paid_at: d.paid_at ? new Date(d.paid_at).toISOString() : undefined, notes: d.notes || undefined,
          comprobante_url: comprobante,
        });
        ok('Pago registrado'); m.close(); reload();
      } catch (ex) { err(ex.message); }
    } },
    el('div', { class: 'form-grid' },
      field({ name: 'amount', label: `Monto (saldo ${$(saldo)})`, type: 'number', step: '0.01', min: '0.01', max: saldo.toFixed(2), value: saldo.toFixed(2), required: true }),
      field({ name: 'method', label: 'Forma de pago', type: 'select', required: true, value: 'transferencia', options: METODOS.map(([value, label]) => ({ value, label })) }),
      field({ name: 'reference', label: 'Referencia' }),
      field({ name: 'paid_at', label: 'Fecha', type: 'datetime-local', value: localInput(new Date()) }),
    ),
    el('div', { class: 'field' }, el('label', {}, 'Comprobante (foto o PDF)'), archivo),
    field({ name: 'notes', label: 'Notas', type: 'textarea' }));
    const m = openModal({ title: 'Registrar pago', body: form, footer: [
      el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
      el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, 'Registrar pago'),
    ] });
  }

  function abrirDeposito(accion, maximo) {
    const titulos = { recibir: 'Recibir depósito', aplicar: 'Aplicar el depósito a la renta', devolver: 'Devolver el depósito' };
    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      const d = readForm(form);
      try {
        await Rentals.deposito(id, { accion, monto: Number(d.monto), metodo: d.metodo || 'efectivo', nota: d.nota || undefined });
        ok(titulos[accion]); m.close(); reload();
      } catch (ex) { err(ex.message); }
    } },
    field({ name: 'monto', label: 'Monto', type: 'number', step: '0.01', min: '0.01', max: maximo.toFixed(2), value: maximo.toFixed(2), required: true }),
    accion !== 'aplicar' ? field({ name: 'metodo', label: 'Forma', type: 'select', value: 'efectivo', options: METODOS.map(([value, label]) => ({ value, label })) }) : null,
    field({ name: 'nota', label: 'Nota (opcional)' }));
    const m = openModal({ title: titulos[accion], body: form, footer: [
      el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
      el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, 'Guardar'),
    ] });
  }

  function abrirCargo() {
    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      const d = readForm(form);
      try { await Rentals.multa(id, { tipo: d.tipo, monto: Number(d.monto), descripcion: d.descripcion }); ok('Cargo agregado'); m.close(); reload(); }
      catch (ex) { err(ex.message); }
    } },
    el('div', { class: 'form-grid' },
      field({ name: 'tipo', label: 'Tipo', type: 'select', required: true, value: atrasoMin > 0 ? 'atraso' : 'dano', options: TIPOS_CARGO.map(([value, label]) => ({ value, label })) }),
      field({ name: 'monto', label: 'Monto', type: 'number', step: '0.01', min: '0.01', required: true }),
    ),
    field({ name: 'descripcion', label: 'Qué pasó (sale en la factura)', required: true, value: atrasoMin > 0 ? `Devolvió ${tarde(atrasoMin)} tarde` : '' }));
    const m = openModal({ title: 'Agregar cargo', body: form, footer: [
      el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
      el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, 'Agregar a la renta'),
    ] });
  }
}

// Cada combo seguido de sus componentes.
function ordenarLineas(items) {
  const out = [];
  for (const l of items.filter((x) => !x.kit_line_id)) {
    out.push(l);
    if (l.es_kit) out.push(...items.filter((x) => x.kit_line_id === l.id));
  }
  return out.concat(items.filter((x) => x.kit_line_id && !out.includes(x)));
}

function cifra(label, valor, tono) {
  return el('div', { class: `rp-cifra ${tono || ''}` }, el('span', {}, label), el('strong', {}, valor));
}
function fila(label, valor) {
  return el('div', { class: 'flex justify-between text-sm' }, el('span', { class: 'muted' }, label), el('span', {}, valor));
}
function tarde(min) {
  min = Number(min);
  if (min >= 1440) return `${Math.floor(min / 1440)} día(s)`;
  if (min >= 60) return `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}`;
  return `${min} min`;
}
function localInput(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
